import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../services/api';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Users, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

const tooltipStyle = {
  backgroundColor: '#1a2035',
  border: '1px solid #1e293b',
  borderRadius: 8,
  fontSize: 12,
  color: '#f0f2f5',
};
const tooltipLabelStyle = { color: '#f0f2f5' };
const tooltipItemStyle = { color: '#8b95a8' };

const SEGMENT_COLORS = {
  'VIP': '#a78bfa',
  'Лояльные': '#6366f1',
  'Активные': '#10b981',
  'Новые': '#06b6d4',
  'Перспективные': '#84cc16',
  'Требуют внимания': '#f59e0b',
  'Засыпающие': '#fb923c',
  'Спящие': '#ef4444',
  'Потерянные': '#6b7280',
};

export default function SegmentsPage() {
  const [rfm, setRfm] = useState(null);
  const [atRisk, setAtRisk] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [riskParams, setRiskParams] = useState({ minBalance: 100, daysInactive: 30 });

  useEffect(() => {
    Promise.all([
      analyticsAPI.rfm(),
      analyticsAPI.atRisk(100, 30),
    ])
      .then(([rfmRes, riskRes]) => {
        setRfm(rfmRes.data);
        setAtRisk(riskRes.data);
      })
      .catch(() => toast.error('Ошибка загрузки аналитики'))
      .finally(() => setLoading(false));
  }, []);

  const loadAtRisk = () => {
    analyticsAPI.atRisk(riskParams.minBalance, riskParams.daysInactive)
      .then((res) => setAtRisk(res.data))
      .catch(() => toast.error('Ошибка'));
  };

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Анализ клиентской базы...</div>;
  if (!rfm) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Нет данных для анализа</div>;

  const pieData = rfm.segments.map((s) => ({
    name: s.name,
    value: s.count,
    color: SEGMENT_COLORS[s.name] || '#6366f1',
  }));

  const activeSegment = selectedSegment
    ? rfm.segments.find((s) => s.name === selectedSegment)
    : null;

  return (
    <div className="animate-in">
      <div className="page-header">
        <h1 className="page-title">RFM-сегментация</h1>
        <p className="page-subtitle">
          Проанализировано {rfm.analyzed_clients} из {rfm.total_clients} клиентов
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="card">
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-secondary)' }}>
            Распределение по сегментам
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                outerRadius={110} paddingAngle={2}
                onClick={(_, idx) => setSelectedSegment(pieData[idx]?.name)}>
                {pieData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} stroke="var(--bg-card)" strokeWidth={2}
                    style={{ cursor: 'pointer' }} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>


        <div className="card" style={{ overflow: 'auto' }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-secondary)' }}>
            Сегменты
          </h3>
          <table className="table">
            <thead>
              <tr>
                <th>Сегмент</th>
                <th>Кол-во</th>
                <th>%</th>
                <th>Ср. баланс</th>
                <th>Ср. выручка</th>
              </tr>
            </thead>
            <tbody>
              {rfm.segments.map((s) => (
                <tr key={s.name}
                  style={{ cursor: 'pointer', background: selectedSegment === s.name ? 'var(--accent-soft)' : undefined }}
                  onClick={() => setSelectedSegment(s.name === selectedSegment ? null : s.name)}>
                  <td>
                    <span style={{
                      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                      background: SEGMENT_COLORS[s.name] || '#6366f1', marginRight: 8
                    }} />
                    {s.name}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)' }}>{s.count}</td>
                  <td>{s.percentage}%</td>
                  <td style={{ fontFamily: 'var(--font-mono)' }}>{s.avg_balance.toLocaleString('ru')}</td>
                  <td style={{ fontFamily: 'var(--font-mono)' }}>{s.avg_monetary.toLocaleString('ru')} ₽</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>


      {activeSegment && (
        <div className="card" style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-secondary)' }}>
            <span style={{
              display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
              background: SEGMENT_COLORS[activeSegment.name], marginRight: 8
            }} />
            {activeSegment.name} — {activeSegment.count} клиентов
          </h3>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Телефон</th>
                  <th>Имя</th>
                  <th>Баланс</th>
                  <th>Дней с покупки</th>
                  <th>Покупок</th>
                  <th>Сумма покупок</th>
                  <th>R</th>
                  <th>F</th>
                  <th>M</th>
                </tr>
              </thead>
              <tbody>
                {activeSegment.clients.map((c) => (
                  <tr key={c.client_id}>
                    <td style={{ fontFamily: 'var(--font-mono)' }}>{c.phone}</td>
                    <td>{c.name || '—'}</td>
                    <td style={{ fontFamily: 'var(--font-mono)' }}>{c.balance.toLocaleString('ru')}</td>
                    <td style={{ fontFamily: 'var(--font-mono)' }}>{c.recency_days}</td>
                    <td style={{ fontFamily: 'var(--font-mono)' }}>{c.frequency}</td>
                    <td style={{ fontFamily: 'var(--font-mono)' }}>{c.monetary.toLocaleString('ru')} ₽</td>
                    <td>{c.r_score}</td>
                    <td>{c.f_score}</td>
                    <td>{c.m_score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}


      <div className="card" style={{ marginTop: 24 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--danger)' }}>
          <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Клиенты в зоне риска
        </h3>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end' }}>
          <div>
            <label className="form-label">Мин. баланс баллов</label>
            <input className="form-input" type="number" style={{ width: 140 }}
              value={riskParams.minBalance}
              onChange={(e) => setRiskParams({ ...riskParams, minBalance: parseInt(e.target.value) || 0 })} />
          </div>
          <div>
            <label className="form-label">Дней без покупок</label>
            <input className="form-input" type="number" style={{ width: 140 }}
              value={riskParams.daysInactive}
              onChange={(e) => setRiskParams({ ...riskParams, daysInactive: parseInt(e.target.value) || 30 })} />
          </div>
          <button className="btn btn-primary" onClick={loadAtRisk}>Найти</button>
        </div>

        {atRisk.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Телефон</th>
                  <th>Имя</th>
                  <th>Баланс</th>
                  <th>Дней без покупок</th>
                  <th>Всего потрачено</th>
                  <th>Сегмент</th>
                </tr>
              </thead>
              <tbody>
                {atRisk.map((c) => (
                  <tr key={c.client_id}>
                    <td style={{ fontFamily: 'var(--font-mono)' }}>{c.phone}</td>
                    <td>{c.name || '—'}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--warning)' }}>
                      {c.balance.toLocaleString('ru')}
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--danger)' }}>{c.days_since_last_order}</td>
                    <td style={{ fontFamily: 'var(--font-mono)' }}>{c.total_spent.toLocaleString('ru')} ₽</td>
                    <td><span className="badge badge-warning">{c.segment}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: 'var(--text-muted)', padding: 20 }}>Клиентов в зоне риска не найдено</p>
        )}
      </div>
    </div>
  );
}